import React, { useState, useRef, useEffect } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Image } from "expo-image";
import { CameraView, useCameraPermissions } from "expo-camera";
import { MaterialIcons } from "@expo/vector-icons";
import { mobAlert } from "../../../mobx/MobAlert";
import * as ImageManipulator from "expo-image-manipulator";
import i18n from "../../../i18n";
import PropTypes from "prop-types";

interface previewObject {
  isPreview: boolean;
  capturedImage: {
    uri: string;
    width: number;
    height: number;
    base64: string;
  } | null;
  isPortrait?: boolean | null;
}

export default function CameraComponent({ takePicture }) {
  const [facing, setFacing] = useState("back");
  const [flash, setFlash] = useState("off");
  const [photoUri, setPhotoUri] = useState(null);
  const [permission, requestPermission] = useCameraPermissions();
  const cameraRef = useRef(null);

  useEffect(() => {
    if (!permission) {
      return;
    }

    if (!permission.granted) {
      mobAlert.setAlert(
        i18n.t("permissions.MSG_PERMISSION_CAMERA_TITLE"),
        i18n.t("permissions.MSG_PERMISSION_CAMERA"),
        i18n.t("generic.ok"),
        () => {
          requestPermission().then(
            (permission) => {
              mobAlert.closeAlert();
              if (permission.granted) {
                mobAlert.openCamera(takePicture);
              } else {
                mobAlert.closeCamera();
              }
            }
          );
        }
      );
    }
  }, [permission, requestPermission, takePicture]);

  if (!permission?.granted) {
    return null;
  }

  function toggleCameraFacing() {
    setFacing(current => (current === "back" ? "front" : "back"));
  }

  function toggleFlash() {
    setFlash(current => (current === "off" ? "on" : "off"));
  }

  async function takePhoto() {
    if (cameraRef.current) {
      const photo = await cameraRef.current.takePictureAsync();
      setPhotoUri(photo.uri);
    }
  }

  function retakePhoto() {
    setPhotoUri(null);
  }

  async function confirmPhoto() {
    // Here you can handle the confirmed photo, e.g., upload it, save it, etc.
    const manipResult = await ImageManipulator.manipulateAsync(
      photoUri,
      [
        {
          resize: {
            height: 1500,
          }
        }
      ],
      {
        base64: true,
        format: ImageManipulator.SaveFormat.JPEG,
        compress: 0.5
      }
    ) as previewObject["capturedImage"];
    await takePicture(manipResult);
    closeCamera();
  }

  function closeCamera() {
    mobAlert.closeCamera();
  }

  if (photoUri) {
    return (
      <View style={styles.previewContainer}>
        <Image source={{ uri: photoUri }} style={styles.previewImage} />
        <TouchableOpacity style={styles.closeButton} onPress={closeCamera}>
          <MaterialIcons name="close" size={50} color="white" />
        </TouchableOpacity>
        <View style={styles.previewButtonContainer}>
          <TouchableOpacity style={[styles.previewButton, styles.retakeButton]} onPress={retakePhoto}>
            <MaterialIcons name="refresh" size={30} color="white" />
          </TouchableOpacity>
          <TouchableOpacity style={[styles.previewButton, styles.confirmButton]} onPress={confirmPhoto}>
            <MaterialIcons name="check" size={30} color="white" />
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CameraView
        ref={cameraRef}
        style={styles.camera}
        facing={facing}
        flash={flash}
      />
      
      {/* UI elements positioned absolutely on top of camera */}
      <TouchableOpacity style={styles.closeButton} onPress={closeCamera}>
        <MaterialIcons name="close" size={50} color="white" />
      </TouchableOpacity>
      
      <View style={styles.buttonContainer}>
        <TouchableOpacity style={styles.iconButton} onPress={toggleCameraFacing}>
          <MaterialIcons name="flip-camera-ios" size={30} color="white" />
        </TouchableOpacity>
        <View style={styles.captureButton}>
          <TouchableOpacity style={styles.innerCircle} onPress={takePhoto} />
        </View>
        <TouchableOpacity style={styles.iconButton} onPress={toggleFlash}>
          <MaterialIcons name={flash === "off" ? "flash-off" : "flash-on"} size={30} color="white" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    backgroundColor: "#000",
  },
  camera: {
    flex: 1,
  },
  buttonContainer: {
    position: "absolute",
    bottom: 30,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    zIndex: 1,
  },
  iconButton: {
    padding: 10,
  },
  captureButton: {
    width: 70,
    height: 70,
    borderRadius: 35,
    borderWidth: 5,
    borderColor: "white",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
  },
  innerCircle: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: "white",
  },
  text: {
    fontSize: 18,
    color: "white",
  },
  closeButton: {
    position: "absolute",
    top: 50,
    right: 10,
    padding: 5,
    borderRadius: 20,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    zIndex: 1,
  },
  previewContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#000",
  },
  previewImage: {
    width: "100%",
    height: "80%",
    resizeMode: "contain",
  },
  previewButtonContainer: {
    paddingTop: 20,
    flexDirection: "row",
    justifyContent: "space-around",
    width: "100%",
  },
  previewButton: {
    width: "40%",
    alignItems: "center",
    padding: 10,
    borderRadius: 5,
  },
  retakeButton: {
    backgroundColor: "red",
  },
  confirmButton: {
    backgroundColor: "green",
  },
});

CameraComponent.propTypes = {
  takePicture: PropTypes.func.isRequired,
};